import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  InjectionToken,
  signal,
  viewChild,
} from '@angular/core';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';

export type CrmPopupAction = 'cta' | 'dismiss';

export interface CrmPopupViewData {
  /** Full self-contained document, rendered server-side at dispatch. */
  html: string;
  style: 'modal' | 'banner';
  /**
   * Called at most once, the first time the popup document speaks over its
   * bridge — proof that the srcdoc parsed and its script ran, i.e. that there
   * is something on screen. The opener acks `shown` off this rather than off
   * host construction: an attached iframe that never renders would otherwise
   * retire a popup the user never saw.
   */
  onPresented(): void;
  /** Called at most once — the popup's outcome. The opener closes the host. */
  onAction(action: CrmPopupAction): void;
}

/** Overlay-hosted (banner) instances get their data via a child injector. */
export const CRM_POPUP_DATA = new InjectionToken<CrmPopupViewData>('CRM_POPUP_DATA');

/**
 * Renders one CRM popup. The payload is a full HTML document rendered
 * server-side at dispatch time, so it is shown in a sandboxed iframe via
 * `srcdoc` — never innerHTML. `allow-scripts` (without `allow-same-origin`)
 * lets the document run its own CTA/dismiss wiring while keeping it
 * origin-isolated from the app. The document reports its outcome with
 * `window.parent.postMessage({ source: 'crm-popup', action })`; anything not
 * verifiably from this component's own iframe is ignored.
 *
 * Hosted two ways by {@link CrmPopupNotificationService}: `modal` inside a
 * `DialogService` dialog (data via DIALOG_DATA), `banner` inside a raw
 * top-center CDK overlay (data via CRM_POPUP_DATA).
 */
@Component({
  selector: 'app-crm-popup',
  templateUrl: './crm-popup.component.html',
  styleUrl: './crm-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrmPopupComponent {
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _frame = viewChild.required<ElementRef<HTMLIFrameElement>>('frame');

  readonly data =
    inject<CrmPopupViewData>(DIALOG_DATA, { optional: true }) ?? inject(CRM_POPUP_DATA);

  // Safe to bypass sanitization: the document renders inside a sandboxed,
  // cross-origin-isolated iframe, so it can't touch the app's DOM or storage.
  readonly srcdoc: SafeHtml = this._sanitizer.bypassSecurityTrustHtml(this.data.html);

  /**
   * Content height the document reported over the bridge, applied to the frame.
   * Null until the first report — the stylesheet's fallback height covers that
   * frame, since an iframe cannot size itself to its content.
   */
  readonly frameHeight = signal<number | null>(null);

  /** `onPresented` is a one-shot: the document reports its height repeatedly. */
  private _presented = false;

  constructor() {
    fromEvent<MessageEvent>(window, 'message')
      .pipe(takeUntilDestroyed())
      .subscribe((event) => this._onMessage(event));
  }

  private _onMessage(event: MessageEvent): void {
    if (event.source !== this._frame().nativeElement.contentWindow) {
      return;
    }
    const data: unknown = event.data;
    if (typeof data !== 'object' || data === null) {
      return;
    }
    const { source, action, height } = data as { source?: unknown; action?: unknown; height?: unknown };
    if (source !== 'crm-popup') {
      return;
    }
    // Anything at all from this iframe means the srcdoc parsed and its script
    // ran — the document is on screen. That, not host construction, is what
    // the opener treats as "shown".
    if (!this._presented) {
      this._presented = true;
      this.data.onPresented();
    }
    if (action === 'resize') {
      if (typeof height === 'number' && Number.isFinite(height) && height > 0) {
        this.frameHeight.set(Math.ceil(height));
      }
      return;
    }
    if (action !== 'cta' && action !== 'dismiss') {
      return;
    }
    this.data.onAction(action);
  }
}
